import { navigate } from "gatsby"
import { TrackJS } from "trackjs"
import client from "../apollo/client"
import { ADD_EVENT_LOG } from "../apollo/mutations"
import {
  CURRENT_ROUTE,
  GET_CURRENT_USER,
  GET_EVENTS_BY,
  TRACK_EVENT,
} from "../apollo/queries"
import VERSION from "../version"
import { getCurrentUser } from "./auth"
import log from "./log"
import { safeReadQuery } from "./user-data"

export const showStartBeacon = () => {
  const onboardingInteracted = window.localStorage.getItem(
    "_onboardingInteracted"
  )
  // Only show a maximum of 3 times
  if (onboardingInteracted) return

  client
    .query({ query: GET_CURRENT_USER })
    .then(result => {
      let settings, accountCreated
      const {
        data: { currentUser },
      } = result
      try {
        settings = JSON.parse(currentUser.settings)
        accountCreated = settings.AccountCreated
          ? new Date(settings.AccountCreated).getTime()
          : new Date().getTime()
        const msSinceAccountCreation = new Date().getTime() - accountCreated

        console.log("TCL: showStartBeacon -> settings", {
          settings,
          accountCreated,
          msSinceAccountCreation,
        })

        if (msSinceAccountCreation > 60000) return
        // Otherwise, show new user help scout beacon
        // window.Beacon &&
        //   window.Beacon("article", "5dc078602c7d3a7e9ae37d1c", { type: "modal" })
        // Set the number of times viewed
        // window.localStorage.setItem("onboardingInteracted", onboardingInteracted + 1)
        console.log("TCL: showStartBeacon -> navigating to onboarding series")
        navigate("/a/first-steps")
      } catch (error) {
        console.warn("TCL: showStartBeacon -> error parsing user settings", {
          result,
        })
      }
    })
    .catch(error => console.warn("TCL: showStartBeacon -> error", error))
}

export const trackPageView = location => {
  // Track user's current page
  const { currentRoute } = safeReadQuery(CURRENT_ROUTE, {
    currentRoute: null,
  })
  // Prevent sending repeat data with multiple renders of the same route
  if (location.pathname !== currentRoute) {
    // Log track page view
    log("log", [
      "Tracking: Page View",
      { path: location.pathname, current: currentRoute },
    ])
    // Set path to currentRoute state value
    client.writeData({ data: { currentRoute: location.pathname } })
    // Send page view to Segment.io
    window.analytics && window.analytics.page(location.pathname)
  }
}

const doTriggerEventServer = (name, data, metadata, settings) => {
  if (!metadata) metadata = {}

  client
    .query({
      query: TRACK_EVENT,
      variables: {
        email: getCurrentUser().email,
        eventName: name,
        eventData: JSON.stringify(data),
      },
      fetchPolicy: "network-only",
    })
    .then(result => {
      const { data: response } = result
      if (response) {
        console.log(
          `TCL: doTriggerEventServer -> event tracked (${name}) ${metadata.month} ${metadata.year}`,
          {
            data,
            response,
          }
        )
      } else {
        console.warn(
          `TCL: doTriggerEventServer -> event tracking failed (${name}) ${metadata.month} ${metadata.year} `,
          {
            data,
            response,
          }
        )
      }
    })

  const eventLogVars = {
    date: new Date().toString(),
    month: metadata.month || "",
    year: `${metadata.year}` || "",
    entryDate: metadata.entryDate || "",
    eventName: name,
    eventData: JSON.stringify(data),
    userInformation: JSON.stringify(settings),
  }

  log("log", ["TCL Tracking: Track Event", { name, data, eventLogVars }])
  client.mutate({ mutation: ADD_EVENT_LOG, variables: eventLogVars })
}

const filterDuplicateEventsLocal = (name, data) => {
  let isTriggered = false
  const triggeredEvents = JSON.parse(localStorage.getItem("events")) || []

  triggeredEvents.forEach(event => {
    if (event.name === name && event.data === JSON.stringify(data))
      isTriggered = true
  })

  if (!isTriggered) {
    triggeredEvents.push({ name, data: JSON.stringify(data) })
    localStorage.setItem("events", JSON.stringify(triggeredEvents))
  }

  return isTriggered
}

const filterDuplicateEvents = (name, data, metadata, settings) => {
  if (filterDuplicateEventsLocal(name, data)) return

  client
    .query({
      query: GET_EVENTS_BY,
      variables: {
        user: getCurrentUser().email,
        eventName: name,
        month: metadata.month,
        year: `${metadata.year}`,
      },
      fetchPolicy: "network-only",
    })
    .then(result => {
      const { data: response } = result
      const events = response.userEventsBy
      if (events.length === 0) {
        console.log(
          `TCL: filterDuplicateEvents -> no event found (${name}) ${metadata.month} ${metadata.year} - tracking event...`,
          {
            response,
            user: getCurrentUser().email,
            eventName: name,
            month: metadata.month,
            year: `${metadata.year}`,
          }
        )
        doTriggerEventServer(name, data, metadata, settings)
      } else {
        console.log(
          `TCL: filterDuplicateEvents -> ${name} rejected (exists) ${metadata.month} ${metadata.year} `,
          events
        )
      }
    })
}

export const trackEvent = (name, data, metadata) => {
  if (!name || !data) {
    log("warn", [
      "Tracking: Analytics `track` was called without a name and/or data",
      { name, data },
    ])
    return
  }

  // Check an artifact that would exist on existing user device, allowing us to appropriately
  // apply this function with backwards compatability
  const isOldUser = window.localStorage.getItem("isAuthPolling")

  client
    .query({ query: GET_CURRENT_USER })
    .then(result => {
      let settings
      const {
        data: { currentUser },
      } = result
      try {
        settings = JSON.parse(currentUser.settings)
        if (!settings) {
          console.warn(
            "TCL: trackEvent -> user settings from database were empty",
            settings
          )
        }

        // console.log("TCL: trackEvent -> settings from db", settings)
        switch (name) {
          case "Created Entry":
            doTriggerEventServer(name, data, metadata, settings)
            break
          case "Guided Reflection Available":
            // Is monthly review
            if (
              metadata.month &&
              metadata.year &&
              settings.monthlyReviewsAvailable &&
              !settings.monthlyReviewsAvailable.includes(
                `${metadata.month} ${metadata.year}`
              )
            )
              filterDuplicateEvents(name, data, metadata, settings)
            else
              console.log(
                `TCL: trackEvent -> Guided Reflection Available rejected (exists) ${metadata.month} ${metadata.year} `
              )
            break
          case "Guided Reflection Started":
            filterDuplicateEvents(name, data, metadata, settings)
            break
          case "Guided Reflection Completed":
            // Is monthly review
            if (
              metadata.month &&
              metadata.year &&
              settings.monthlyReviewsCompleted &&
              !settings.monthlyReviewsCompleted.includes(
                `${metadata.month} ${metadata.year}`
              )
            )
              filterDuplicateEvents(name, data, metadata, settings)
            else {
              if (metadata.month)
                console.log(
                  `TCL: trackEvent -> Guided Reflection Completed rejected (exists) ${metadata.month} ${metadata.year} `
                )
            }

            // Is yearly review
            if (
              !metadata.month &&
              metadata.year &&
              settings.yearlyReviewsCompleted &&
              !settings.yearlyReviewsCompleted.includes(metadata.year)
            )
              filterDuplicateEvents(name, data, metadata, settings)
            else {
              if (!metadata.month && metadata.year)
                console.log(
                  `TCL: trackEvent -> Guided Reflection Completed rejected (exists) ${metadata.year} `
                )
            }

            break
          default:
            doTriggerEventServer(name, data, metadata, settings)
            break
        }
      } catch (error) {
        console.warn("TCL: trackEvent -> error parsing user settings", {
          result,
        })
      }
    })
    .catch(error =>
      console.warn("TCL: trackEvent GET_CURRENT_USER query -> error", error)
    )

  // doTriggerEvent(name, data, metadata)
}

export const setupTrackJS = () => {
  // Check if already installed
  // if (window.localStorage.getItem("track_js_installed")) return
  const currentUser = getCurrentUser()

  const config = {
    token: "9ae6e58f115946ce8c38c52614b2ab26",
    // Set the current user
    userId: currentUser.email,
    // Set Agent version number
    version: VERSION.number,
    // Define TrackJS Application
    application: window.location.href.includes("localhost")
      ? "reflection-app-dev"
      : "reflection-app-prod",

    // Custom onError callback
    onError: function(payload) {
      // Console log error group
      console.groupCollapsed(`TCL: TrackJS -> onError`)
      console.log("TCL: payload -  window.TrackJS -> onErrored", payload)
      console.log(
        "TCL: payload -  window.TrackJS -> Error Stack",
        payload.stack
      )
      console.groupEnd()

      // Push user email as metadata
      payload.metadata.push({
        key: "user",
        value: currentUser.email,
      })

      // Push application version as metadata
      payload.metadata.push({
        key: "version",
        value: VERSION.number,
      })

      // Ignore errors from local environments.
      if (payload.url.indexOf("localhost") >= 0) {
        return false
      }

      // Push the error to TrackJS
      return true
    },
    console: {
      display: window.location.host === "www.my.reflection.app" ? false : true,
    },
  }

  // If this process has not been run before (old version) then
  // delete the TrackJS current installation
  if (window.TrackJS) {
    if (window.TrackJS.isInstalled()) {
      // log("log", ["🚀 TCL: TrackJS (installed) -> Run Config", config])
      window.TrackJS.configure(config)
    } else {
      // log("log", ["🚀 TCL: TrackJS (uninstalled) -> Run Config", config])
      window.TrackJS.install(config)
    }
  } else {
    window.TrackJS = TrackJS

    if (TrackJS.isInstalled()) {
      // log("log", ["🚀 TCL: TrackJS (installed) -> Run Config", config])
      TrackJS.configure(config)
    } else {
      // log("log", ["🚀 TCL: TrackJS (uninstalled) -> Run Config", config])
      TrackJS.install(config)
    }
  }

  // Install TrackJS
  window.localStorage.setItem("track_js_installed", true)
}
